import React, { useEffect, useMemo, useState } from 'react';
import authService, { AccessProfile } from '../services/auth.service';
import ModuleAccessGrid from '../components/ModuleAccessGrid';
import './AccessProfiles.css';

interface ModuleOption {
  id: string;
  name: string;
}

interface Account {
  id: number | string;
  firstName?: string;
  lastName?: string;
  role?: string;
  active?: boolean;
}

interface AccessProfilesProps {
  modules: ModuleOption[];
  users: Account[];
  onProfilesChanged: () => void;
}

const personName = (person: { firstName?: string; lastName?: string }) =>
  `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Sans nom';

type AccessMap = Record<string, any>;

const parseAccess = (raw: unknown): AccessMap => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? { ...(raw as AccessMap) } : {};
};

const AccessProfiles: React.FC<AccessProfilesProps> = ({ modules, users, onProfilesChanged }) => {
  const [profiles, setProfiles] = useState<AccessProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<AccessProfile | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [access, setAccess] = useState<AccessMap>({});
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [achatsLists, setAchatsLists] = useState<{ id: string; name: string }[]>([]);
  const [achatsListsError, setAchatsListsError] = useState<string | null>(null);
  const [captationsSites, setCaptationsSites] = useState<{ id: string; name: string }[]>([]);
  const [captationsSitesError, setCaptationsSitesError] = useState<string | null>(null);
  const [vaultGroups, setVaultGroups] = useState<{ id: string; name: string }[]>([]);
  const [vaultGroupsError, setVaultGroupsError] = useState<string | null>(null);

  const load = async () => {
    try {
      const response = await authService.getAccessProfiles();
      if (response.success && response.profiles) setProfiles(response.profiles);
      else setError(response.message || 'Impossible de charger les profils');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Impossible de charger les profils');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const formOpen = creating || !!editing;

  useEffect(() => {
    if (!formOpen) return;
    let cancelled = false;
    const loadCatalog = async (url: string) => {
      const token = authService.getToken();
      const response = await fetch(url, {
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || data?.message || 'chargement impossible');
      const raw = Array.isArray(data?.data) ? data.data : [];
      return raw
        .map((item: { id?: string; name?: string }) => ({
          id: String(item?.id || '').trim(),
          name: String(item?.name || '').trim()
        }))
        .filter((item: { id: string; name: string }) => item.id && item.name)
        .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
    };
    const refresh = () => {
      loadCatalog('/api/achats/lists')
        .then((list) => {
          if (cancelled) return;
          setAchatsLists(list);
          setAchatsListsError(list.length === 0 ? 'Aucune liste d’achats pour le moment.' : null);
        })
        .catch(() => {
          if (!cancelled) setAchatsListsError('Impossible de charger les listes d’achats.');
        });
      loadCatalog('/api/captations/sites')
        .then((list) => {
          if (cancelled) return;
          setCaptationsSites(list);
          setCaptationsSitesError(list.length === 0 ? 'Aucun dossier de captation pour le moment.' : null);
        })
        .catch(() => {
          if (!cancelled) setCaptationsSitesError('Impossible de charger les dossiers de captation.');
        });
      loadCatalog('/api/vault/groups')
        .then((list) => {
          if (cancelled) return;
          setVaultGroups(list);
          setVaultGroupsError(list.length === 0 ? 'Aucun groupe Vault pour le moment.' : null);
        })
        .catch(() => {
          if (!cancelled) setVaultGroupsError('Impossible de charger les groupes Vault.');
        });
    };
    refresh();
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [formOpen]);

  const toggleCatalog = (moduleId: 'achats' | 'captations' | 'vault', itemId: string, enabled: boolean) => {
    const key = moduleId === 'achats' ? 'lists' : moduleId === 'captations' ? 'sites' : 'groups';
    setAccess((prev) => {
      const current = prev[moduleId] && typeof prev[moduleId] === 'object' && !Array.isArray(prev[moduleId])
        ? prev[moduleId]
        : {};
      const selected: string[] = Array.isArray(current[key])
        ? current[key].map((item: unknown) => String(item).trim()).filter(Boolean)
        : [];
      const next = enabled
        ? Array.from(new Set([...selected, itemId]))
        : selected.filter((item) => item !== itemId);
      return { ...prev, [moduleId]: { ...current, [key]: next } };
    });
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return profiles.filter((profile) => !q || profile.name.toLowerCase().includes(q));
  }, [profiles, query]);

  const people = useMemo(() => (
    [...users]
      .filter((user) => String(user.role || '').toLowerCase() !== 'admin')
      .sort((a, b) => personName(a).localeCompare(personName(b), 'fr'))
  ), [users]);

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    setName('');
    setDescription('');
    setColor('#3b82f6');
    setMemberIds([]);
    setAccess({});
    setError(null);
  };

  const openEdit = (profile: AccessProfile) => {
    setCreating(false);
    setEditing(profile);
    setName(profile.name);
    setDescription(profile.description || '');
    setColor(profile.color || '#3b82f6');
    setMemberIds((profile.userIds || []).map((id) => Number(id)));
    setAccess(parseAccess(profile.moduleAccess));
    setError(null);
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (editing && editing.memberCount > 0) {
      const ok = window.confirm(
        `Enregistrer « ${trimmed} » met à jour les droits de ${editing.memberCount} compte${editing.memberCount > 1 ? 's' : ''} qui utilisent ce profil. Les émissions déjà choisies sur un compte sont conservées. Les autres comptes ne changent pas.`
      );
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { name: trimmed, description, color, moduleAccess: access, userIds: memberIds };
      const response = editing
        ? await authService.updateAccessProfile(editing.id, payload)
        : await authService.createAccessProfile(payload);
      if (!response.success) {
        setError(response.message || 'Enregistrement impossible');
        return;
      }
      await load();
      onProfilesChanged();
      close();
      const updated = response.updatedUsers || 0;
      setNotice(updated > 0
        ? `Profil enregistré. ${updated} compte${updated > 1 ? 's' : ''} mis à jour.`
        : 'Profil enregistré. Aucun compte existant n’a été modifié.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (profile: AccessProfile) => {
    const message = profile.memberCount > 0
      ? `Supprimer « ${profile.name} » ? Les ${profile.memberCount} comptes liés gardent leurs droits actuels, ils ne suivront juste plus ce profil.`
      : `Supprimer « ${profile.name} » ?`;
    if (!window.confirm(message)) return;
    try {
      const response = await authService.deleteAccessProfile(profile.id);
      if (!response.success) {
        setError(response.message || 'Suppression impossible');
        return;
      }
      await load();
      onProfilesChanged();
      setNotice('Profil supprimé. Les droits des comptes n’ont pas été réécrits.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Suppression impossible');
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-toolbar">
        <input className="ap-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un profil…" />
        <button type="button" className="ap-btn ap-btn-primary" onClick={openCreate}>+ Nouveau profil</button>
      </div>
      {notice && <div className="ap-notice">{notice}</div>}
      {error && !formOpen && <div className="ap-error">{error}</div>}
      {loading ? <div className="ap-empty">Chargement des profils…</div> : (
        <div className="ap-list">
          {visible.map((profile) => (
            <article key={profile.id} className="ap-card" style={{ borderLeft: `4px solid ${profile.color || '#3b82f6'}` }}>
              <div>
                <h3>{profile.name}</h3>
                <p>{profile.description || 'Sans description'}</p>
                <div className="mag-chips" style={{ marginTop: 10 }}>
                  {(profile.userIds || []).map((id) => {
                    const person = users.find((user) => Number(user.id) === Number(id));
                    if (!person) return null;
                    return <span key={id} className="mag-chip is-on">{personName(person)}</span>;
                  })}
                </div>
              </div>
              <div className="ap-card-side">
                <span>{profile.memberCount} compte{profile.memberCount > 1 ? 's' : ''}</span>
                <button type="button" className="ap-btn" onClick={() => openEdit(profile)}>Modifier</button>
                <button type="button" className="ap-btn ap-btn-danger" onClick={() => remove(profile)}>Supprimer</button>
              </div>
            </article>
          ))}
          {visible.length === 0 && <div className="ap-empty">Aucun profil. Crée-en un pour préremplir les droits d’un nouveau compte.</div>}
        </div>
      )}

      {formOpen && (
        <div className="ap-modal">
          <form className="ap-modal-card" onSubmit={save}>
            <div className="ap-modal-head">
              <h3>{editing ? 'Modifier le profil' : 'Nouveau profil'}</h3>
              <button type="button" className="ap-btn" onClick={close}>Fermer</button>
            </div>
            <div className="ap-modal-body">
            <p className="ap-help">
              Les personnes sélectionnées reçoivent les droits de ce profil. Les émissions déjà choisies sur un compte sont conservées.
            </p>
            {error && <div className="ap-error">{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 10 }}>
              <label className="ap-field">
                Nom
                <input value={name} onChange={(event) => setName(event.target.value)} required placeholder="Journaliste, monteur, régie…" />
              </label>
              <label className="ap-field">
                Couleur
                <input type="color" value={color} onChange={(event) => setColor(event.target.value)} aria-label="Couleur du profil" style={{ height: 42, padding: 4, cursor: 'pointer' }} />
              </label>
            </div>
            <label className="ap-field">
              Description
              <input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Optionnel" />
            </label>
            <div style={{ marginTop: 16 }}>
              <div className="mag-group-label">Membres</div>
              <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                Une personne n’a qu’un seul profil. La sélection applique les droits de ce profil.
              </p>
              <div className="mag-chips">
                {people.map((person) => {
                  const id = Number(person.id);
                  const on = memberIds.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`mag-chip${on ? ' is-on' : ''}`}
                      onClick={() => setMemberIds((prev) => (on ? prev.filter((item) => item !== id) : [...prev, id]))}
                    >
                      {personName(person)}
                    </button>
                  );
                })}
              </div>
            </div>
            <ModuleAccessGrid
              modules={modules}
              access={access}
              onChange={setAccess}
              renderExtra={(moduleId) => {
                if (moduleId === 'achats') {
                  const selected: string[] = Array.isArray(access.achats?.lists)
                    ? access.achats.lists.map((item: unknown) => String(item))
                    : [];
                  return (
                    <div style={{ marginTop: 10 }}>
                      <div className="mag-group-label">Listes visibles</div>
                      <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                        Cochez les listes ouvertes par ce profil. Une nouvelle liste apparaît ici dès sa création.
                      </p>
                      {achatsListsError && (
                        <p style={{ color: '#f87171', fontSize: 11, margin: '0 0 8px' }}>{achatsListsError}</p>
                      )}
                      <div className="mag-chips">
                        {achatsLists.map((list) => (
                          <button
                            key={list.id}
                            type="button"
                            className={`mag-chip${selected.includes(list.id) ? ' is-on' : ''}`}
                            onClick={() => toggleCatalog('achats', list.id, !selected.includes(list.id))}
                          >
                            {list.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (moduleId === 'vault') {
                  const selected: string[] = Array.isArray(access.vault?.groups)
                    ? access.vault.groups.map((item: unknown) => String(item))
                    : [];
                  return (
                    <div style={{ marginTop: 10 }}>
                      <div className="mag-group-label">Groupes visibles</div>
                      <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                        Cochez les groupes Vault ouverts par ce profil. Un nouveau groupe apparaît ici dès sa création.
                      </p>
                      {vaultGroupsError && (
                        <p style={{ color: '#f87171', fontSize: 11, margin: '0 0 8px' }}>{vaultGroupsError}</p>
                      )}
                      <div className="mag-chips">
                        {vaultGroups.map((group) => (
                          <button
                            key={group.id}
                            type="button"
                            className={`mag-chip${selected.includes(group.id) ? ' is-on' : ''}`}
                            onClick={() => toggleCatalog('vault', group.id, !selected.includes(group.id))}
                          >
                            {group.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (moduleId === 'captations') {
                  const selected: string[] = Array.isArray(access.captations?.sites)
                    ? access.captations.sites.map((item: unknown) => String(item))
                    : [];
                  return (
                    <div style={{ marginTop: 10 }}>
                      <div className="mag-group-label">Dossiers visibles</div>
                      <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 8px' }}>
                        Cochez les dossiers ouverts par ce profil. Un nouveau dossier apparaît ici dès sa création.
                      </p>
                      {captationsSitesError && (
                        <p style={{ color: '#f87171', fontSize: 11, margin: '0 0 8px' }}>{captationsSitesError}</p>
                      )}
                      <div className="mag-chips">
                        {captationsSites.map((site) => (
                          <button
                            key={site.id}
                            type="button"
                            className={`mag-chip${selected.includes(site.id) ? ' is-on' : ''}`}
                            onClick={() => toggleCatalog('captations', site.id, !selected.includes(site.id))}
                          >
                            {site.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            </div>
            <div className="ap-modal-actions">
              <button type="button" className="ap-btn" onClick={close}>Annuler</button>
              <button type="submit" className="ap-btn ap-btn-primary" disabled={saving}>{editing ? 'Enregistrer et appliquer' : 'Créer le profil'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AccessProfiles;
